import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { form, FormField, required } from '@angular/forms/signals';
import { Router } from '@angular/router';
import '@fmr-ap152550/dom/lib/dom-authenticators.min.js';
import '@fmr-ap152550/dom/lib/dom-core.min.js';
import '@fmr-ap152550/dom/lib/dom-preset-react.min.js';
import { IdentifyUserResponse, PasswordStatusCheckResponse } from 'app/model/passwordStatusCheckRespoonse';
import { constants } from '../constants';
import { LoginService } from '../login/login.service';

enum PasswordStatus {
  ACT = 'ACT',
  IAC = 'IAC',
  BLK = 'BLK',
  RST = 'RST',
}

const passwordStatusCodeMap: Record<PasswordStatus, readonly number[]> = {
  [PasswordStatus.ACT]: [1200, 1205],
  [PasswordStatus.IAC]: [1203],
  [PasswordStatus.BLK]: [1203],
  [PasswordStatus.RST]: [1205, 1201, 1401],
};

interface ConfirmIdentityForm {
  username: string;
}

@Component({
  selector: 'app-confirm-identity',
  templateUrl: './confirm-identity.component.html',
  styleUrls: ['./confirm-identity.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormField],
})
export class ConfirmIdentity {
  readonly #router = inject(Router);
  readonly #loginService = inject(LoginService);
  readonly isLoading = signal(false);
  readonly errorMessageText = signal('');
  readonly acceptablePasswordStatusForReset = new Set(
    Object.values(passwordStatusCodeMap).flat(),
  );

  readonly #confirmIdentityModel = signal<ConfirmIdentityForm>({
    username: '',
  });

  readonly confirmIdentityForm = form(this.#confirmIdentityModel, (schemaPath) => {
    required(schemaPath.username, { message: 'Username is required' });
  });

  returnToLogin() {
    this.#router.navigate(['login']);
  }

  // Need this
  userExists(event: Event) {
    event.preventDefault();

    this.isLoading.set(true);
    this.#loginService.clearSession().subscribe({
      next: () => {
        this.#loginService.identifyUser(this.confirmIdentityForm.username().value()).subscribe({
          next: (resp: IdentifyUserResponse) => {
            const info = resp;
            const statusCode = info?.responseBaseInfo?.status?.code;
            if (statusCode === constants.sessionStatusCode.success) {
              // allowInteraction: 'false', is not available in new sdk 
              // we took resetPasswordEndpoint out as per our research c&c should provide this
              this.#loginService.checkPasswordStatus().subscribe({
                next: (psStatusRes: PasswordStatusCheckResponse) => {
                  const mappedPasswordStatusRes = psStatusRes;
                  const passwordStatusCode = mappedPasswordStatusRes?.responseBaseInfo?.status?.code;

                  if (passwordStatusCode !== null && passwordStatusCode !== undefined && this.acceptablePasswordStatusForReset.has(passwordStatusCode)) {
                    if (passwordStatusCodeMap[PasswordStatus.ACT].includes(passwordStatusCode)) {
                      // Active have cppStatus true per reset password flow document
                      this.#router.navigate(['/login'], { queryParams: { event_type: 'password_reset', 'ccpStatus': false } })

                    } else if (passwordStatusCodeMap[PasswordStatus.IAC].includes(passwordStatusCode) ||
                      passwordStatusCodeMap[PasswordStatus.BLK].includes(passwordStatusCode) ||
                      passwordStatusCodeMap[PasswordStatus.RST].includes(passwordStatusCode)) {
                      // For IAC, BLK, RST status, Password status, allow user to proceed with reset without showing cpp
                      this.#router.navigate(['/login'], { queryParams: { event_type: 'password_reset' } })
                    }
                    else {
                      this.errorMessageText.set(
                        mappedPasswordStatusRes?.responseBaseInfo?.status?.message || 'There is a problem. Please reach out to system admin'
                      );
                    }
                  } else {

                    this.errorMessageText.set(
                      mappedPasswordStatusRes?.responseBaseInfo?.status?.message || 'There is a problem. Please reach out to system admin'
                    );
                  }
                },
                error: (err: HttpErrorResponse) => {
                  this.errorMessageText.set('Please contact fidelityloginsupport@fmr.com for assistance');
                  // this.confirmIdentityForm.username().value.set('');
                  this.isLoading.set(false);
                }
              });
            } else {
              this.errorMessageText.set('Please contact fidelityloginsupport@fmr.com for assistance');
              this.confirmIdentityForm.username().value.set('');
              this.isLoading.set(false);
            }
          },
          error: (e: HttpErrorResponse) => {
            console.error(e);
            this.errorMessageText.set(
              'Please contact fidelityloginsupport@fmr.com for assistance (status code 1491)',
            );
            this.isLoading.set(false);
          },
        });
      },
      error: (err: HttpErrorResponse) => { console.log(err); }
    });

  }
}
